import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { initiatePayment, fetchPaymentStatus, confirmPayment } from '../api/paymentApi';

const Payment: React.FC = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();

  const [amount, setAmount] = useState<number>(0);
  const [method, setMethod] = useState<string>('CREDIT_CARD');
  const [paymentId, setPaymentId] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!reservationId) return;

    const fetchReservationDetails = async () => {
      try {
        setLoading(true);
        setAmount(10000); // 임시 금액 설정
      } catch (err) {
        setError('예약 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchReservationDetails();
  }, [reservationId]);

  const handleInitiatePayment = async () => {
    if (!reservationId) return;

    try {
      setLoading(true);
      const result = await initiatePayment({ reservationId, amount, method });
      setPaymentId(result.paymentId);
      setPaymentStatus(result.status);
      setError('');

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (err: any) {
      setError(err?.message || '결제 요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckPaymentStatus = async () => {
    if (!paymentId) return;

    try {
      setLoading(true);
      const statusResp = await fetchPaymentStatus(paymentId);
      setPaymentStatus(statusResp.status);
    } catch (err: any) {
      setError(err?.message || '결제 상태를 확인하지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!reservationId || !paymentId) return;
  
    try {
      setLoading(true);
      // confirmPayment 함수에 필요한 모든 속성을 전달
      await confirmPayment({ reservationId, paymentId, method, amount });
      setError('');
      alert('결제가 성공적으로 완료되었습니다!');
      navigate('/reservation');
    } catch (err: any) {
      setError(err?.message || '결제 확인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!reservationId) {
    return <div>유효하지 않은 예약 ID입니다.</div>;
  }

  return (
    <div className="p-6 max-w-lg mx-auto">
      <h2 className="text-2xl font-bold mb-4">예약 결제 #{reservationId}</h2>

      {loading && <div className="mb-2 text-blue-600">처리 중...</div>}
      {error && <div className="mb-2 text-red-500">{error}</div>}

      <div className="mb-4">
        <label className="block font-semibold mb-1">결제 금액</label>
        <input
          type="number"
          className="border p-2 rounded w-full"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
        />
      </div>

      <div className="mb-4">
        <label className="block font-semibold mb-1">결제 방법 선택</label>
        <select
          className="border p-2 rounded w-full"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        >
          <option value="CREDIT_CARD">신용카드</option>
          <option value="KAKAO_PAY">카카오페이</option>
          <option value="NAVER_PAY">네이버페이</option>
          <option value="PAYPAL">페이팔</option>
        </select>
      </div>

      {paymentId && (
        <div className="mb-4">
          <p className="text-sm text-gray-500">
            결제 ID: {paymentId} (상태: {paymentStatus || 'N/A'})
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleInitiatePayment}
          className="bg-blue-600 text-white px-4 py-2 rounded"
          disabled={loading}
        >
          결제 요청
        </button>
        <button
          onClick={handleCheckPaymentStatus}
          className="bg-yellow-500 text-white px-4 py-2 rounded"
          disabled={loading || !paymentId}
        >
          상태 확인
        </button>
        <button
          onClick={handleConfirmPayment}
          className="bg-green-500 text-white px-4 py-2 rounded"
          disabled={loading || !paymentId}
        >
          결제 확인
        </button>
      </div>
    </div>
  );
};

export default Payment;